import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createDeferredAuthListener } from '../lib/deferredAuthListener';
import { registerBrowserSession, readBrowserSessionKey } from '../lib/browserSession';
import { supabase } from '../supabaseClient';
import { showToast } from '../lib/toast';
import { clearSupabaseAuthStorage, signOutAndClearSiteScopeSession } from '../lib/authSession-v2';

const AuthContext = createContext();

const MAX_SESSIONS = 2;
const AUTH_SESSION_TIMEOUT_MS = 20000;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const STALE_SESSION_MS = 24 * 60 * 60 * 1000; // 24 hours
let initialSessionPromise = null;

function isSupabaseLockError(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return msg.includes('auth-token') && (
    msg.includes('another request stole it') ||
    msg.includes('was released') ||
    msg.includes('lock:') ||
    msg.includes('locks:')
  );
}

function isInvalidRefreshToken(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return msg.includes('invalid refresh token') || msg.includes('refresh token not found');
}

function isAuthTimeout(error) {
  return error?.name === 'AuthSessionTimeoutError';
}

function sameSessionIdentity(a, b) {
  return (a?.access_token || null) === (b?.access_token || null) &&
    (a?.user?.id || null) === (b?.user?.id || null);
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getJwtIssuedAt(accessToken) {
  try {
    const rawPayload = String(accessToken || '').split('.')[1] || '';
    const paddedPayload = rawPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(rawPayload.length / 4) * 4, '=');
    const payload = JSON.parse(atob(paddedPayload));
    return payload?.iat ? new Date(payload.iat * 1000) : null;
  } catch {
    return null;
  }
}

async function isSessionRevokedByProfile(session) {
  if (!session?.user?.id) return false;
  const { data, error } = await supabase
    .from('profiles')
    .select('force_sign_out_at')
    .eq('id', session.user.id)
    .maybeSingle();

  if (error || !data?.force_sign_out_at) return false;

  const revokedAt = new Date(data.force_sign_out_at);
  const issuedAt = getJwtIssuedAt(session.access_token);
  if (!issuedAt || Number.isNaN(revokedAt.getTime())) return false;

  return revokedAt > issuedAt;
}

function withAuthTimeout(promise) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error('Initial auth session lookup timed out');
      error.name = 'AuthSessionTimeoutError';
      reject(error);
    }, AUTH_SESSION_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

function getInitialSession() {
  if (!initialSessionPromise) {
    initialSessionPromise = withAuthTimeout(supabase.auth.getSession())
      .catch(async (error) => {
        if (isInvalidRefreshToken(error) || isAuthTimeout(error)) {
          console.warn('[SiteScope] Auth bootstrap failed; clearing local auth state:', error.message || error);
          clearSupabaseAuthStorage();
          return { data: { session: null }, error: null };
        }
        if (!isSupabaseLockError(error)) throw error;
        await delay(250);
        return withAuthTimeout(supabase.auth.getSession()).catch(retryError => {
          if (isInvalidRefreshToken(retryError) || isAuthTimeout(retryError)) {
            console.warn('[SiteScope] Auth bootstrap retry failed; clearing local auth state:', retryError.message || retryError);
            clearSupabaseAuthStorage();
            return { data: { session: null }, error: null };
          }
          if (isSupabaseLockError(retryError)) {
            console.warn('[SiteScope] Auth bootstrap lock race persisted; continuing without blocking app boot:', retryError.message || retryError);
            return { data: { session: null }, error: null };
          }
          throw retryError;
        });
      })
      .finally(() => {
        initialSessionPromise = null;
      });
  }
  return initialSessionPromise;
}

async function cleanStaleSessions(userId) {
  const cutoff = new Date(Date.now() - STALE_SESSION_MS).toISOString();
  await supabase
    .from('user_sessions')
    .delete()
    .eq('user_id', userId)
    .lt('last_seen_at', cutoff);
}

// Returns true if the current session was booted (shouldn't happen on fresh login,
// but guards against edge cases)
async function enforceSessionLimit(userId, currentSessionKey, isCurrent) {
  const { data: sessions, error } = await supabase
    .from('user_sessions')
    .select('id, session_id, last_seen_at')
    .eq('user_id', userId)
    .order('last_seen_at', { ascending: false });

  if (!isCurrent() || error || !sessions) return false;

  if (sessions.length > MAX_SESSIONS) {
    const toDelete = sessions.slice(MAX_SESSIONS);
    const idsToDelete = toDelete.map(s => s.id);

    await supabase
      .from('user_sessions')
      .delete()
      .in('id', idsToDelete);

    const currentBooted = toDelete.some(s => s.session_id === currentSessionKey);
    return currentBooted;
  }

  return false;
}

// Check if our session record still exists — detects being booted by another login
async function isSessionStillValid(sessionKey) {
  const { data, error } = await supabase
    .from('user_sessions')
    .select('id')
    .eq('session_id', sessionKey)
    .maybeSingle();

  if (error) return true; // assume valid on error (don't boot on network blip)
  return data !== null;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);
  const activeSessionRef = useRef(null);
  const signingOutRef = useRef(false);
  const signingOutUserRef = useRef(null);

  const signOut = async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    signingOutUserRef.current = activeSessionRef.current?.user?.id || null;
    activeSessionRef.current = null;
    setSession(null);
    setLoading(true);
    try {
      await signOutAndClearSiteScopeSession({ isCurrent: () => activeSessionRef.current === null });
    } finally {
      signingOutRef.current = false;
      signingOutUserRef.current = null;
      setLoading(false);
    }
  };

  // Bootstrap and auth events share one generation guard. An older lookup or
  // validation must never replace the account delivered by a newer auth event.
  useEffect(() => {
    let cancelled = false;
    let authEventSeen = false;
    const authListener = createDeferredAuthListener({
      onSession: s => {
        activeSessionRef.current = s;
        setSession(current => sameSessionIdentity(current, s) ? current : s);
        setLoading(signingOutRef.current);
      },
      onSignedIn: async (s, isCurrent) => {
        const isActive = () => isCurrent() && activeSessionRef.current?.user?.id === s.user.id;
        if (!isActive()) return;
        const revoked = await isSessionRevokedByProfile(s);
        if (!isActive()) return;
        if (revoked) {
          await signOut();
          showToast("You've been signed out for security. Please sign in again.");
          return;
        }
        await cleanStaleSessions(s.user.id);
        if (!isActive()) return;
        let sessionKey;
        try {
          sessionKey = await registerBrowserSession(s.user.id, supabase, isActive);
        } catch (error) {
          console.error('Session register error:', error);
          return;
        }
        if (!isActive() || !sessionKey) return;
        const booted = await enforceSessionLimit(s.user.id, sessionKey, isActive);
        if (!isActive()) return;
        if (booted) {
          await signOut();
          showToast("You've been signed out because your account is active on too many devices.");
        }
      },
      onError: error => console.error('Session validation error:', error),
    });
    const handleAuthEvent = (event, s) => {
      // Reject the departing account before the deferred listener can advance
      // its generation or cancel validation queued for a newer account.
      if (signingOutRef.current && s?.user?.id && s.user.id === signingOutUserRef.current) return;
      authListener.handle(event, s);
    };
    // Keep callbacks synchronous: database validation is deferred until Supabase
    // releases its auth lock. INITIAL_SESSION follows the same guarded path.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      authEventSeen = true;
      handleAuthEvent(event, s);
    });
    getInitialSession().then(({ data: { session: s } }) => {
      if (!cancelled && !authEventSeen) handleAuthEvent('INITIAL_SESSION', s);
    }).catch(error => {
      if (cancelled || authEventSeen) return;
      console.error('Session initialisation error:', error);
      if (isInvalidRefreshToken(error) || isAuthTimeout(error)) clearSupabaseAuthStorage();
      handleAuthEvent('INITIAL_SESSION', null);
    });

    return () => {
      cancelled = true;
      authListener.dispose();
      subscription.unsubscribe();
    };
  }, []);

  // Heartbeat: keep last_seen_at fresh and detect being booted by another device
  useEffect(() => {
    if (!session) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
      return;
    }

    let cancelled = false;
    const isCurrent = () => !cancelled && sameSessionIdentity(activeSessionRef.current, session);
    const tick = async () => {
      if (!isCurrent()) return;
      const key = readBrowserSessionKey(session.user.id);

      // Check if another device booted us
      const valid = key ? await isSessionStillValid(key) : false;
      if (!isCurrent()) return;
      const revoked = await isSessionRevokedByProfile(session);
      if (!isCurrent()) return;
      if (!valid || revoked) {
        clearInterval(heartbeatRef.current);
        await signOut();
        showToast(revoked
          ? "You've been signed out for security. Please sign in again."
          : !key
            ? "Your session has expired. Please sign in again."
            : "You've been signed out because your account is active on another device.");
        return;
      }

      // Refresh last_seen_at
      await supabase
        .from('user_sessions')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('session_id', key).eq('user_id', session.user.id);
    };

    heartbeatRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(heartbeatRef.current);
    };
  }, [session]);

  const user = session?.user || null;

  return (
    <AuthContext.Provider value={{ session, user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
